import { CommissionTermsPostPayload, CommissionTermsUpdatePayload, COMMISSION_TERM_TYPE } from '../api/types';
import { FormModel, Product } from '../pages/CommissionDetailsPage/CommissionDetailsPage.types';
import { FIELDS, FILTER_NAMES } from '../fields/types';
import { format } from 'utils/date';
import { CommissionDataForView } from './types';
import { Option } from 'form/components/Select/Select';


const optionTranslations = (tier: string) => {
  const optionsDict: {
    [key: string]: string;
  } = {
    LIVEMORE_1: 'LiveMore 1',
    LIVEMORE_2: 'LiveMore 2',
    LIVEMORE_3: 'LiveMore 3',
    LIVEMORE_3_PLUS: 'LiveMore 3 Plus',
    LIVEMORE_4: 'LiveMore 4',
    LIVEMORE_4_PLUS: 'LiveMore 4 Plus',
    DEBT_CONSOLIDATION_100: '100% Debt Consolidation',
  };
  if (tier in optionsDict) {
    return optionsDict[tier];
  }
  return `${tier.slice(0, 1).toUpperCase()}${tier.slice(1).toLowerCase()}`;
};

export const mapApiDataForView = (data: CommissionDataForView): FormModel => {
  return {
    [FIELDS.COMMISSION_TERM_TYPE]: data.type,
    [FIELDS.COMMISSION_NAME]: data.name,
    [FIELDS.FEE]: data.fee,
    [FIELDS.UPFRONT_FEE]: data.upfrontFee,
    [FIELDS.ONGOING_FEE]: data.ongoingFee,
    [FIELDS.FEE_END_DATE]: format(data.feeEndDate as NonNullable<string>).fromBackendToView(),
    [FIELDS.PRODUCTS]: data.products,
    [FIELDS.FILTERS]: {
      [FILTER_NAMES.PRODUCT_TIER]: '',
      [FILTER_NAMES.PURPOSE_OF_THE_LOAN]: '',
      [FILTER_NAMES.PRODUCT_TYPE]: '',
      [FILTER_NAMES.FIXED_RATE_LENGTH]: '',
    },
  };
};

export const mapFormDataForSave = (data: Omit<FormModel, 'filters'>): CommissionTermsPostPayload => {
  return {
    type: data[FIELDS.COMMISSION_TERM_TYPE],
    name: data[FIELDS.COMMISSION_NAME],
    fee:
      data[FIELDS.COMMISSION_TERM_TYPE] === COMMISSION_TERM_TYPE.STANDARD ||
      data[FIELDS.COMMISSION_TERM_TYPE] === COMMISSION_TERM_TYPE.ENHANCED
        ? Number(data[FIELDS.FEE])
        : null,
    upfrontFee:
      data[FIELDS.COMMISSION_TERM_TYPE] === COMMISSION_TERM_TYPE.ONGOING ? Number(data[FIELDS.UPFRONT_FEE]) : null,
    ongoingFee:
      data[FIELDS.COMMISSION_TERM_TYPE] === COMMISSION_TERM_TYPE.ONGOING ? Number(data[FIELDS.ONGOING_FEE]) : null,
    feeEndDate:
      data[FIELDS.FEE_END_DATE] && data[FIELDS.COMMISSION_TERM_TYPE] === COMMISSION_TERM_TYPE.ONGOING
        ? format(data[FIELDS.FEE_END_DATE] as NonNullable<string>).fromViewToRequest()
        : null,
    products: data.products.map(mapProductForSave),
  };
};

export const mapFormDataForUpdate = (data: Omit<FormModel, 'filters'>): CommissionTermsUpdatePayload => {
  return {
    name: data[FIELDS.COMMISSION_NAME],
    fee:
      data[FIELDS.COMMISSION_TERM_TYPE] === COMMISSION_TERM_TYPE.STANDARD ||
      data[FIELDS.COMMISSION_TERM_TYPE] === COMMISSION_TERM_TYPE.ENHANCED
        ? Number(data[FIELDS.FEE])
        : null,
    upfrontFee:
      data[FIELDS.COMMISSION_TERM_TYPE] === COMMISSION_TERM_TYPE.ONGOING ? Number(data[FIELDS.UPFRONT_FEE]) : null,
    ongoingFee:
      data[FIELDS.COMMISSION_TERM_TYPE] === COMMISSION_TERM_TYPE.ONGOING ? Number(data[FIELDS.ONGOING_FEE]) : null,
    feeEndDate:
      data[FIELDS.FEE_END_DATE] && data[FIELDS.COMMISSION_TERM_TYPE] === COMMISSION_TERM_TYPE.ONGOING
        ? format(data[FIELDS.FEE_END_DATE] as NonNullable<string>).fromViewToRequest()
        : null,
    products: data.products.map(mapProductForSave),
  };
};

const mapProductForSave = (product: Product) => {
  return product.code;
};

export const mapFiltersBasedOnAvailableProducts = (products: Product[]) => {
  const productTiers = Array.from(new Set(products.map((product) => product.tier))).sort((a: any, b: any) =>
    a.localeCompare(b),
  );
  const productGroups = Array.from(new Set(products.map((product) => product.productGroup)));
  const tiers = productGroups.concat(productTiers).filter((item): item is string => !!item);
  const loanPurposes = Array.from(new Set(products.map((product) => product.loanPurpose))).filter((item) => item);
  const productTypes = Array.from(new Set(products.map((product) => product.type))).filter((item) => item);
  const fixedRateLengths = Array.from(new Set(products.map((product) => product.fixRateTerm))).filter((item) => item);

  const defaultOption: Option = {
    selectedRender: <span>Show all</span>,
    label: 'Show all',
    render: <span>Show all</span>,
    name: '',
    selected: false,
  };

  return {
    productTier: [
      defaultOption,
        ...tiers.map((tier) => ({ name: tier, label: optionTranslations(tier), selected: false })),
    ],
    purposeOfTheLoan: [
      defaultOption,
      ...loanPurposes.map((loanPurpose) => ({ name: loanPurpose, label: loanPurpose, selected: false })),
    ],
    productType: [defaultOption, ...productTypes.map((type) => ({ name: type, label: type, selected: false }))],
    fixedRateLength: [
      defaultOption,
      ...fixedRateLengths.map((rateLength) => ({ name: rateLength, label: rateLength, selected: false })),
    ],
  };
};
